import { uiSurface } from "shared/ui/uiSurface"
import {
  Box,
  Button,
  IconButton,
  Paper,
  Stack,
  Typography,
} from "@mui/material"

import {
  closestCenter,
  DndContext,
  DragEndEvent,
  DragOverEvent,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core"
import { sortableKeyboardCoordinates } from "@dnd-kit/sortable"
import { ChevronLeft, ChevronRight, Refresh, Tune } from "@mui/icons-material"
import { APIProvider } from "@vis.gl/react-google-maps"
import dayjs from "dayjs"
import { debounce } from "lodash-es"
import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { DatePickerWrapper } from "src/features/shared-module/components/DatePickerWrapper"
import { Parcel } from "src/features/shared-module/icons"
import { RouteSummary } from "src/features/shared-module/models"
import { showToast } from "src/features/shared-module/slices"
import { wsService } from "src/utils/WebSocketService"
import {
  appConstants,
  OrderStatusEnum,
} from "../../features/shared-module/constants"
import {
  useAssignOrderToEmployeeMutation,
  useBatchAssignOrdersMutation,
  useGetAllEmployeesQuery,
  useGetAllHaulersQuery,
  // useGetOrderLayoutConfigQuery,
  useLazyGetOrderRouteByIdQuery,
} from "../apis"
import { OrderContextProvider, useOrderContex } from "../context"
import { useAppDispatch, useAppSelector } from "../hooks"
import { useAllOrders } from "../hooks/useAllOrders"
import {
  batchAssignInProgressSelector,
  clearSelection,
  haulerListSelector,
  moveOrder,
  reorderInSameSection,
  selectedOrderIdsSelector,
  setActiveDragItem,
  setBatchAssignInProgress,
  setEmployeeList,
  setExpandableSections,
  setHaulerList,
  setLoading,
  // setOrderCardLayoutConfig,
  setOrderist,
  setOrderRoutes,
  setOrdersLoading,
} from "../slices/dashboard.slice"
import {
  AssignmentProgressDialog,
  BatchAssignmentOrder,
} from "./AssignmentProgressDialog"
import { HaulerEmployeeList } from "./HaulerEmployeeList"
import { HorizontalCollapsiblePaper } from "./HorizontalCollapsiblePaper"
import { OrderSummaryMapContainer } from "./OrderSummaryMapContainer"
import { OrdertListByStatus } from "./OrdertListByStatus"

export const Dashboard: FC = () => {
  const { t } = useTranslation()
  const expandableSections = useAppSelector(
    (state) => state.dashboard.expandableSections,
  )
  const [date, setDate] = useState<string>(
    dayjs().format(appConstants.dateFormat),
  )
  const [debouncedDate, setDebouncedDate] = useState<string>(date)
  const datePickerRef = useRef<
    HTMLElement & { openCalendar: (target: HTMLElement) => void }
  >(null)

  const {
    data: haulerResponse,
    error: haulerError,
    isFetching: isHaulerFetching,
    refetch: refetchHaulers,
  } = useGetAllHaulersQuery(null)
  const {
    data: employeeResponse,
    isFetching: isEmployeeLoading,
    error: employeeError,
    refetch: refetchEmployees,
  } = useGetAllEmployeesQuery(null)

  /* TODO: remove static id later */
  // const {
  //   data: orderConfigResponse,
  //   isFetching: isOrderConfigLoading,
  //   error: orderConfigError,
  // } = useGetOrderLayoutConfigQuery(218)

  const {
    data: orderList,
    isFetching: isOrderFetching,
    error: orderError,
    refetch: refetchOrders,
  } = useAllOrders(debouncedDate)

  const [getRouteSummaryDetail] = useLazyGetOrderRouteByIdQuery()
  const [assignOrderToEmployee] = useAssignOrderToEmployeeMutation()
  const [batchAssignOrders] = useBatchAssignOrdersMutation()

  const dispatch = useAppDispatch()
  const haulerList = useAppSelector(haulerListSelector)
  const selectedOrderIds = useAppSelector(selectedOrderIdsSelector)
  const batchAssignInProgress = useAppSelector(batchAssignInProgressSelector)

  // Batch assignment dialog state
  const [batchDialogOpen, setBatchDialogOpen] = useState(false)
  const [batchDialogOrders, setBatchDialogOrders] = useState<
    BatchAssignmentOrder[]
  >([])
  const [batchDialogEmployeeName, setBatchDialogEmployeeName] = useState("")

  const isInitialFetching = isHaulerFetching || isEmployeeLoading
  const isInitialError = employeeError || haulerError // || orderConfigError

  const isFetching = isInitialFetching || isOrderFetching
  const hasOrderData = !!orderList

  useEffect(() => {
    // isLoading = only hauler/employee initial fetch (fast, blocks entire UI)
    dispatch(setLoading(isInitialFetching))
  }, [dispatch, isInitialFetching])

  useEffect(() => {
    // isOrdersLoading = orders still loading (slow, only blocks order lists)
    dispatch(setOrdersLoading(isOrderFetching && !hasOrderData))
  }, [dispatch, isOrderFetching, hasOrderData])

  useEffect(() => {
    if (isInitialFetching || isInitialError) {
      return
    }
    if (employeeResponse) dispatch(setEmployeeList(employeeResponse))
    if (haulerResponse) dispatch(setHaulerList(haulerResponse))
    // if (orderConfigResponse)
    //   dispatch(setOrderCardLayoutConfig(orderConfigResponse.data))
  }, [
    dispatch,
    employeeResponse,
    haulerResponse,
    isInitialError,
    isInitialFetching,
  ])

  const handleDebouncedDate = useMemo(
    () =>
      debounce((newDate) => {
        setDebouncedDate(newDate)
      }, 500),
    [],
  )

  // Keep WebSocketService in sync with current orders date for optimistic MQTT patching
  useEffect(() => {
    wsService.setCurrentOrdersDateArg(debouncedDate)
  }, [debouncedDate])

  useEffect(() => {
    if (
      !haulerList.length ||
      !debouncedDate ||
      isOrderFetching ||
      orderError ||
      !orderList
    ) {
      return
    }
    dispatch(setOrderist(orderList))
  }, [
    debouncedDate,
    dispatch,
    haulerList.length,
    isOrderFetching,
    orderError,
    orderList,
  ])

  useEffect(() => {
    if (!orderList?.length) {
      if (!isOrderFetching) {
        dispatch(
          setOrderRoutes({
            routeDetail: null,
            routeSummaryId: null,
            isLoading: false,
          }),
        )
      }
      return
    }

    const routeSummaryIds = orderList.filter((order) => order.routeSummaryId)
    const uniqueRouteSummaryIds: number[] = [
      ...new Set(
        routeSummaryIds.map((order) => order.routeSummaryId as number),
      ),
    ]

    uniqueRouteSummaryIds.forEach((routeSummaryId) => {
      dispatch(
        setOrderRoutes({
          routeDetail: null,
          isLoading: true,
          routeSummaryId,
        }),
      )
    })

    uniqueRouteSummaryIds.map((routeSummaryId) =>
      getRouteSummaryDetail(routeSummaryId)
        .unwrap()
        .then(({ data: routeDetail }) => {
          const routeSummary = {
            ...routeDetail,
            raw: routeDetail.raw ? JSON.parse(routeDetail.raw as string) : null,
          }
          dispatch(
            setOrderRoutes({
              routeDetail: routeSummary as RouteSummary,
              isLoading: false,
              routeSummaryId,
            }),
          )
        })
        .catch(() => {
          dispatch(
            setOrderRoutes({
              routeDetail: null,
              isLoading: false,
              routeSummaryId,
              error: true,
            }),
          )
        }),
    )
  }, [dispatch, getRouteSummaryDetail, isOrderFetching, orderList])

  const onDateChange = (action: "future" | "past") => {
    let dayJsDate = dayjs(date)
    if (action === "past") {
      dayJsDate = dayJsDate.subtract(1, "day")
    } else if (action === "future") {
      dayJsDate = dayJsDate.add(1, "day")
    }
    const newDate = dayJsDate.format(appConstants.dateFormat)
    setDate(newDate)
    handleDebouncedDate(newDate)
  }

  const handleRefreshAll = () => {
    refetchHaulers()
    refetchEmployees()
    refetchOrders()
  }

  const handleToggle = (section: "order" | "employee") => {
    dispatch(setExpandableSections(section))
  }

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )

  const onDragOver = (event: DragOverEvent) => {
    const { active, over } = event
    if (!over || !orderList) return

    const fromOrderId = active.id
    const toOrderId = over.id

    const fromContainerId = active.data.current?.sortable?.containerId
    const toContainerId = over.data.current?.sortable?.containerId

    if (
      !fromContainerId ||
      !toContainerId ||
      fromContainerId === toContainerId
    ) {
      return
    }

    dispatch(
      moveOrder({
        fromContainerId: fromContainerId,
        toContainerId: toContainerId,
        fromOrderId: fromOrderId,
        toOrderId: toOrderId,
      }),
    )
  }

  const handleBatchAssign = useCallback(
    async (
      orderIds: number[],
      haulerEmployeeId: number,
      employeeName: string,
    ) => {
      if (!orderList) return

      const orderMap = orderIds.reduce((map, id) => {
        map.set(
          id,
          orderList.find((o) => o.id === id),
        )
        return map
      }, new Map<number, any>())
      const dialogOrders: BatchAssignmentOrder[] = orderIds.map((id) => {
        return {
          orderId: id,
          reference: orderMap.get(id)?.reference || `#${id}`,
          status: "pending" as const,
        }
      })

      setBatchDialogOrders(dialogOrders)
      setBatchDialogEmployeeName(employeeName)
      setBatchDialogOpen(true)
      dispatch(setBatchAssignInProgress(true))

      try {
        const assignments = orderIds.map((orderId) => ({
          orderId,
          haulerEmployeeId,
        }))

        const response = await batchAssignOrders({ assignments }).unwrap()

        // Update dialog with results
        const updatedOrders = dialogOrders.map((dialogOrder) => {
          const result = response.results.find(
            (r) => r.orderId === dialogOrder.orderId,
          )
          if (result) {
            return {
              ...dialogOrder,
              status: result.ok ? ("success" as const) : ("error" as const),
              error: result.error,
            }
          }
          return {
            ...dialogOrder,
            status: "error" as const,
            error: "No response",
          }
        })
        setBatchDialogOrders(updatedOrders)

        const { summary } = response
        if (summary.failed === 0) {
          dispatch(
            showToast({
              variant: "success",
              message: t("batchAssignSuccess", {
                count: summary.success,
                employeeName,
              }),
            }),
          )
        } else {
          dispatch(
            showToast({
              variant: "warning",
              message: t("batchAssignPartial", {
                success: summary.success,
                failed: summary.failed,
              }),
            }),
          )
        }
      } catch {
        const updatedOrders = dialogOrders.map((o) => ({
          ...o,
          status: "error" as const,
          error: t("orderAssignFailed"),
        }))
        setBatchDialogOrders(updatedOrders)
        dispatch(
          showToast({
            variant: "error",
            message: t("orderAssignFailed"),
          }),
        )
      } finally {
        dispatch(setBatchAssignInProgress(false))
        dispatch(clearSelection())
        refetchOrders()
      }
    },
    [batchAssignOrders, dispatch, orderList, refetchOrders, t],
  )

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    dispatch(setActiveDragItem(null))

    if (!over || !orderList || batchAssignInProgress) return

    const fromOrderId = active.id
    const toOrderId = over.id
    const toContainerId: string = over.data.current?.sortable?.containerId

    // Always check for assignment first — the order's server-side state
    // (from RTK Query cache) is the reliable signal, not container IDs
    const orderId = Number(fromOrderId)
    const order = orderList.find((o) => o.id === orderId)
    const isToEmployee =
      toContainerId && !toContainerId.includes("READY_FOR_HAULAGE")

    if (order?.state === OrderStatusEnum.READY_FOR_HAULAGE && isToEmployee) {
      const [haulerEmployeeIdStr] = toContainerId.split("-")
      const haulerEmployeeId = Number(haulerEmployeeIdStr)

      if (!haulerEmployeeId) return

      // Determine which orders to assign: if dragged item is selected, assign all selected
      const isDraggedItemSelected = selectedOrderIds.includes(orderId)
      const orderIdsToAssign =
        isDraggedItemSelected && selectedOrderIds.length > 1
          ? selectedOrderIds
          : [orderId]

      // Find employee name for the dialog
      const employee = employeeResponse?.find((e) => e.id === haulerEmployeeId)
      const employeeName = employee
        ? `${employee.firstName} ${employee.lastName}`.trim()
        : `Employee #${haulerEmployeeId}`

      if (orderIdsToAssign.length > 1) {
        // Batch assignment with progress dialog
        handleBatchAssign(orderIdsToAssign, haulerEmployeeId, employeeName)
      } else {
        // Single assignment (original behavior)
        assignOrderToEmployee({ orderId, haulerEmployeeId })
          .unwrap()
          .then(() => {
            dispatch(
              showToast({
                variant: "success",
                message: t("orderAssignedSuccessfully"),
              }),
            )
          })
          .catch(() => {
            dispatch(
              showToast({
                variant: "error",
                message: t("orderAssignFailed"),
              }),
            )
          })
          .finally(() => {
            dispatch(clearSelection())
            refetchOrders()
          })
      }
      return
    }

    // Handle reorder within same container (non-assignment drops)
    if (fromOrderId !== toOrderId) {
      const fromContainerId: string = active.data.current?.sortable?.containerId
      if (fromContainerId === toContainerId) {
        dispatch(
          reorderInSameSection({
            fromContainerId,
            toContainerId,
            fromOrderId,
            toOrderId,
          }),
        )
      }
    }
  }

  const onDragStart = (event: DragStartEvent) => {
    const { active } = event
    if (!active?.id || !orderList) return
    dispatch(setActiveDragItem(active.id as number))
  }

  const onDragCancel = () => {
    dispatch(setActiveDragItem(null))
  }

  return (
    <APIProvider apiKey={appConstants.googleMapKey}>
      <Box>
        <DndContext
          onDragOver={onDragOver}
          onDragEnd={onDragEnd}
          onDragStart={onDragStart}
          onDragCancel={onDragCancel}
          sensors={sensors}
          collisionDetection={closestCenter}
        >
          <Stack
            {...uiSurface("UI-DSP-011")}
            direction={"row"}
            justifyContent={"space-between"}
            alignItems={"center"}
            marginBottom={1.5}
          >
            <Stack direction={"row"} spacing={0.5} alignItems={"center"}>
              <IconButton
                data-testid="dashboard-date-prev-btn"
                color="primary"
                size="small"
                onClick={() => {
                  onDateChange("past")
                }}
                sx={(theme) => ({
                  border: `1px solid ${theme.palette.divider}`,
                  "&:hover": {
                    borderColor: theme.palette.primary.main,
                    backgroundColor: theme.palette.primary.light,
                  },
                })}
              >
                <ChevronLeft fontSize="small" />
              </IconButton>
              <Button
                data-testid="dashboard-date-display-btn"
                variant="text"
                onClick={(event) => {
                  datePickerRef?.current?.openCalendar(event.currentTarget)
                }}
                sx={(theme) => ({
                  px: 2,
                  py: 0.75,
                  borderRadius: 2,
                  fontWeight: 600,
                  fontSize: "0.9rem",
                  color: theme.palette.text.primary,
                  letterSpacing: "0.01em",
                  "&:hover": {
                    backgroundColor: theme.palette.primary.light,
                  },
                })}
              >
                {date}
              </Button>
              <IconButton
                data-testid="dashboard-date-next-btn"
                color="primary"
                size="small"
                onClick={() => {
                  onDateChange("future")
                }}
                sx={(theme) => ({
                  border: `1px solid ${theme.palette.divider}`,
                  "&:hover": {
                    borderColor: theme.palette.primary.main,
                    backgroundColor: theme.palette.primary.light,
                  },
                })}
              >
                <ChevronRight fontSize="small" />
              </IconButton>
              {dayjs(date).isSame(dayjs(), "day") && (
                <Typography
                  variant="caption"
                  sx={(theme) => ({
                    ml: 1,
                    px: 1.5,
                    py: 0.25,
                    borderRadius: 99,
                    backgroundColor: theme.palette.primary.main,
                    color: "#fff",
                    fontWeight: 600,
                    fontSize: "0.65rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.06em",
                  })}
                >
                  {t("today")}
                </Typography>
              )}
              <DatePickerWrapper
                ref={datePickerRef}
                value={date}
                onChange={(newDate: string) => {
                  setDate(newDate)
                  setDebouncedDate(newDate)
                }}
                showInput={false}
              />
            </Stack>
            <Stack direction={"row"} alignItems={"center"} spacing={2}>
              <Stack
                direction={"row"}
                spacing={0.75}
                alignItems={"center"}
                data-testid="dashboard-order-count"
                sx={(theme) => ({
                  px: 2,
                  py: 0.5,
                  borderRadius: 99,
                  backgroundColor: theme.palette.background.paper,
                  border: `1px solid ${theme.palette.divider}`,
                })}
              >
                <Parcel
                  sx={(theme) => ({
                    fontSize: 18,
                    color: theme.palette.secondary.main,
                  })}
                />
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 700, fontVariantNumeric: "tabular-nums" }}
                >
                  {orderList?.length || 0}
                </Typography>
              </Stack>
              <IconButton
                data-testid="dashboard-refresh-btn"
                size="small"
                onClick={handleRefreshAll}
                disabled={isFetching}
                sx={(theme) => ({
                  border: `1px solid ${theme.palette.divider}`,
                  transition: "all 0.2s ease",
                  "&:hover": {
                    borderColor: theme.palette.primary.main,
                    backgroundColor: theme.palette.primary.light,
                  },
                  "& .MuiSvgIcon-root": {
                    transition: "transform 0.3s ease",
                  },
                  "&:hover .MuiSvgIcon-root": {
                    transform: "rotate(180deg)",
                  },
                })}
              >
                <Refresh fontSize="small" />
              </IconButton>
            </Stack>
          </Stack>

          <Stack
            direction={"row"}
            height={"calc(100vh - 132px)"}
            width={"100%"}
            spacing={2}
          >
            <Paper
              {...uiSurface("UI-DSP-012")}
              data-testid="dashboard-map-container"
              elevation={0}
              sx={(theme) => ({
                flexGrow: 2,
                borderRadius: 3,
                border: `1px solid ${theme.palette.divider}`,
                overflow: "hidden",
                "& > div > div": {
                  borderRadius: "inherit",
                },
                "& > div": {
                  borderRadius: "inherit",
                },
                // The map sits inside a display:contents UI-DSP-013 marker wrapper, one
                // level deeper than the two rules above reach.
                "& > [data-ui] > div > div": {
                  borderRadius: "inherit",
                },
              })}
            >
              <OrderSummaryMapContainer date={debouncedDate} />
            </Paper>
            <OrderContextProvider>
              <HorizontalCollapsiblePaper
                ui="UI-DSP-015"
                expanded={expandableSections["order"]}
                handleToggle={() => handleToggle("order")}
                collapsedWidth={120}
                expandedWidth={630}
              >
                <OrdertListByStatus expanded={expandableSections["order"]} />
              </HorizontalCollapsiblePaper>
              <HorizontalCollapsiblePaper
                ui="UI-DSP-028"
                handleToggle={() => handleToggle("employee")}
                expanded={expandableSections["employee"]}
                collapsedWidth={500}
                expandedWidth={650}
                customHeader={
                  <CustomHeaderWithFilterIcon handleToggle={handleToggle} />
                }
              >
                <HaulerEmployeeList />
              </HorizontalCollapsiblePaper>
            </OrderContextProvider>
          </Stack>
        </DndContext>
        <AssignmentProgressDialog
          open={batchDialogOpen}
          employeeName={batchDialogEmployeeName}
          orders={batchDialogOrders}
          onClose={() => setBatchDialogOpen(false)}
        />
      </Box>
    </APIProvider>
  )
}

const CustomHeaderWithFilterIcon: FC<{
  handleToggle: (section: "order" | "employee") => void
}> = ({ handleToggle }) => {
  const { isFilterExpanded, setIsFilterExpanded } = useOrderContex()
  const isEmployeeSectionExpanded = useAppSelector(
    (state) => state.dashboard.expandableSections.employee,
  )
  return (
    <Stack
      spacing={0.75}
      direction={"row"}
      justifyContent={"flex-end"}
      sx={{
        paddingY: "3px",
      }}
    >
      <IconButton
        data-testid="dashboard-filter-btn"
        size="small"
        onClick={() => setIsFilterExpanded(!isFilterExpanded)}
        sx={(theme) => ({
          backgroundColor: isFilterExpanded
            ? theme.palette.secondary.main
            : theme.palette.secondary.light,
          color: isFilterExpanded ? "#fff" : theme.palette.secondary.main,
          transition: "all 0.2s ease",
          "&:hover": {
            backgroundColor: isFilterExpanded
              ? theme.palette.secondary.dark
              : theme.palette.secondary.main,
            color: "#fff",
          },
        })}
      >
        <Tune fontSize="small" />
      </IconButton>
      <IconButton
        data-testid="dashboard-employee-toggle-btn"
        size="small"
        sx={(theme) => ({
          border: `1px solid ${theme.palette.divider}`,
          "&:hover": {
            borderColor: theme.palette.primary.main,
            backgroundColor: theme.palette.primary.light,
          },
        })}
        onClick={() => handleToggle("employee")}
      >
        {isEmployeeSectionExpanded ? (
          <ChevronRight fontSize="small" />
        ) : (
          <ChevronLeft fontSize="small" />
        )}
      </IconButton>
    </Stack>
  )
}
